import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { getPetSelector } from '@modules/subscriptions/core/selectors';
import { KTSVG, toAbsoluteUrl } from '@shared';
import { PetProfileUpdateModal } from '../pet-profile-update/PetProfileUpdateModal';
import { usePetUpdateRequired, useMarkCompletedOnUpdateSuccess } from './usePetUpdateRequired';
import { PET_UPDATE_REQUIRED_DISMISSED_EVENT, getDismissedStorageKey } from './constants';
import { readStorageFlag } from './utils';

/**
 * Persistent reminder shown everywhere (mounted in MasterLayout) for customers
 * flagged `update-required` who dismissed the intro modal. Stays visible until
 * the corrective update completes.
 */
export const PetUpdateIssueBanner: React.FC = () => {
  const intl = useIntl();
  const pet = useSelector(getPetSelector);
  const isPuppy = pet?.dog_stage === 'puppy';
  const { shouldShow: updateRequired, petId, markCompleted } = usePetUpdateRequired();

  const shouldShow = updateRequired && isPuppy;

  const [showRecalcModal, setShowRecalcModal] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);

  useMarkCompletedOnUpdateSuccess(showRecalcModal, markCompleted);

  const storageKey = useMemo(() => (petId != null ? getDismissedStorageKey(petId) : null), [petId]);

  // Reveal after the intro modal was dismissed (this session or a prior one).
  useEffect(() => {
    if (readStorageFlag(storageKey)) setWasDismissed(true);
  }, [storageKey]);

  useEffect(() => {
    const handler = () => setWasDismissed(true);
    window.addEventListener(PET_UPDATE_REQUIRED_DISMISSED_EVENT, handler);
    return () => window.removeEventListener(PET_UPDATE_REQUIRED_DISMISSED_EVENT, handler);
  }, []);

  if (!shouldShow || !wasDismissed) return null;

  const handleClick = () => setShowRecalcModal(true);
  const handleRecalcModalClose = () => setShowRecalcModal(false);

  return (
    <>
      {!showRecalcModal && (
        <div className="puppy-transition-banner">
          <div className="calorie-gauge__pet-msg-content">
            <KTSVG path={toAbsoluteUrl('/media/svg/general/orange-info-icon.svg')} />
            <p className="hidden md:block">
              <FormattedMessage
                id="PET_UPDATE.NEEDED_TEXT"
                values={{ gender: pet?.gender === 'f' ? 'f' : 'm' }}
              />
            </p>
            <p className="md:hidden">
              <FormattedMessage
                id="PET_UPDATE.BANNER.SHORT"
                values={{
                  // "für" takes the accusative in German, so the nameless
                  // fallback has to be inflected for it rather than being a
                  // bare noun. EN and FR resolve both cases to one form.
                  name: pet?.name || intl.formatMessage({ id: 'GLOBAL.YOUR_DOG.ACC' }),
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </p>
            <button
              type="button"
              className="ml-auto flex-shrink-0 relative overflow-hidden w-fit z-[1] rounded-full px-[17px] py-[13px] lg:text-[14px] lg:leading-[20px] text-[16px] leading-[22px] whitespace-nowrap font-bold text-white bg-primary hover:bg-[#005f58] transition duration-200 before:absolute before:inset-0 before:z-[1] before:pointer-events-none before:bg-[linear-gradient(90deg,_#fff0_0,_#fffc_50%,_#80bae800_99%,_#7db9e800)] before:animate-highlight"
              onClick={handleClick}>
              <FormattedMessage id="PET_UPDATE.CTA" />
            </button>
          </div>
        </div>
      )}

      <PetProfileUpdateModal
        isOpen={showRecalcModal}
        onClose={handleRecalcModalClose}
        mode="recalculation"
      />
    </>
  );
};
